import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useWriteGate } from '../lib/writeGate';

/**
 * P2a — mobile bottom tab bar (maquette 006 `.p-tabs`).
 *
 * SCOPE
 * - Rendered ONLY at ≤768px by a CSS media query (never by JS resizing); above
 *   that the desktop sidebar is the navigation and this node computes to
 *   `display: none`.
 * - Below 900px the sidebar is ALREADY an off-canvas drawer (hamburger +
 *   overlay, `menuOpen` in AppLayout) — so the bar does not duplicate it:
 *   `More` opens that SAME drawer, which lists every sidebar section with its
 *   own permission gates and collapse state. One source of navigation truth.
 *
 * TABS — Home · 🕹️ Games · [＋ FAB] · My profile · More
 * - Active state is derived from the pathname so a nested route lights up its
 *   tab too (a plain `to="/games"` prefix match would miss `/games/...`).
 * - The FAB is the contextual action: it always means "new show" and is
 *   HIDDEN when the write gate refuses it (read-only / showcase / no band) —
 *   a button that would 403 is worse than no button.
 * - Signed-out surfaces (login, signup, share links) render no tab bar.
 */
interface MobileTabBarProps {
  /** Opens the existing sidebar drawer (= the full "More" sheet). */
  onOpenMore: () => void;
  /** Whether that drawer is currently open (reflected on the More tab). */
  moreOpen: boolean;
  /**
   * LOGO-HOME (2026-11-04) — the contextual home target, computed ONCE by
   * AppLayout (`sidebarHomeHref`) and shared with the sidebar brand link so
   * the logo and this tab can never lead to different places: the band
   * landing (Day-of / Route map), the multi-band Route map, or the band grid.
   * Defaults to `/home` so the component stays usable on its own (tests, any
   * future host) exactly as before.
   */
  homeHref?: string;
}

export default function MobileTabBar({ onOpenMore, moreOpen, homeHref = '/home' }: MobileTabBarProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { canWrite } = useWriteGate();

  if (!user) return null;

  // `/band/:slug/home` is the band landing (it immediately redirects to Day-of
  // or Route map), so it counts as Home too — otherwise the tab would flicker
  // unlit during the redirect.
  const homeOn =
    pathname === '/' ||
    pathname === '/home' ||
    /\/home\/?$/.test(pathname) ||
    pathname === homeHref;
  // On tab bar, Games replaces Shows (2026-11-04): /games is a top-level route.
  const gamesOn = /^\/games(\/|$)/.test(pathname);
  const teamOn = /\/team(\/|$)/.test(pathname);
  const cls = (on: boolean) => `mtab${on ? ' on' : ''}`;
  // The My profile tab goes STRAIGHT to the signed-in user's own card when the
  // account is linked to a person (`/tour/team/:id` carries the team_member
  // id). A non-linked account (billing/owner without a Team member, a viewer
  // with read-only account) falls back to the Team LIST — the historical
  // behaviour — so the tab never 404s. The active state (`teamOn`) already
  // matches both `/tour/team` and `/tour/team/:id`.
  const teamLink = user.team_member_id ? `/tour/team/${user.team_member_id}` : '/tour/team';

  return (
    <nav className="mobile-tabbar" aria-label={t('tabs.label')}>
      <Link to={homeHref} className={cls(homeOn)} aria-current={homeOn ? 'page' : undefined}>
        <span className="mtab-ic" aria-hidden="true">🏠</span>
        {t('tabs.home')}
      </Link>

      <Link to="/games" className={cls(gamesOn)} aria-current={gamesOn ? 'page' : undefined} data-mobile-tab="games">
        <span className="mtab-ic" aria-hidden="true">🕹️</span>
        {t('tabs.games')}
      </Link>

      {canWrite && (
        <button
          type="button"
          className="mtab-fab"
          onClick={() => navigate('/tour/shows/new')}
          aria-label={t('shows.newShow')}
        >
          <span aria-hidden="true">＋</span>
        </button>
      )}

      <Link to={teamLink} className={cls(teamOn)} aria-current={teamOn ? 'page' : undefined}>
        <span className="mtab-ic" aria-hidden="true">🧑‍🤝‍🧑</span>
        {t('tabs.myProfile')}
      </Link>

      <button
        type="button"
        className={`mtab${moreOpen ? ' on' : ''}`}
        onClick={onOpenMore}
        aria-expanded={moreOpen}
        aria-controls="app-sidebar"
      >
        <span className="mtab-ic" aria-hidden="true">☰</span>
        {t('tabs.more')}
      </button>
    </nav>
  );
}
